import { useStaticQuery, graphql } from "gatsby"
import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from "react"
import { Link } from "react-scroll"
import { useDebounce } from "@react-hook/debounce"
import { UIContext } from "../layout"
import {
  HeaderWrapper,
  HeaderInner,
  ImageContainer,
  RightButtons,
  HamburgerIcon,
  Banner,
  Navigation,
} from "./header.style"
import useLightboxManager from "../hooks/useLightboxManager"
import ContactLightbox from "../contact-lightbox"
import ContactSection from "../contact"
import externalLinks from "../../../external-links"
import DropdownMenu from "./dropdown-menu"
import NavigationConfig from "../../../navigation-config"
import breakpoints from "../breakpoints"
import Spacer from "../../../design-system/atoms/layout-primitives/spacer"

//distance to scroll from the top before applying background-color to the header
const OPAQUE_BACKGROUND_THRESHOLD = 64

//distance to scroll to toggle hiding / showing the header
const SCROLL_THRESHOLD = 75

const Header = ({
  showBackground,
  staticHeader = false,
  contactLightbox = false,
  bannerText = "",
}) => {
  const mappedinLogoSrc = useStaticQuery(graphql`
    query {
      allFile(filter: { relativePath: { eq: "mappedin-logo.svg" } }) {
        edges {
          node {
            publicURL
          }
        }
      }
    }
  `).allFile.edges[0].node.publicURL

  const {
    state: { breakpoint },
    dispatch,
  } = useContext(UIContext)

  //Position where we started scrolling up or down
  const [scrollAnchor, setScrollAnchor] = useState(0)

  //Current scroll Y position
  const [scrollPos, setScrollPos] = useDebounce(0, 25, true)

  const [isHidden, setIsHidden] = useState(false)

  const [showSidebarNav, setShowSidebarNav] = useState(true)

  useEffect(() => {
    function handleResize() {
      setShowSidebarNav(
        window.matchMedia(`(max-width: ${breakpoints.lgPlus})`).matches
      )
    }

    window.addEventListener("resize", handleResize)
    // run once at page load to set proper navbar state
    handleResize()
  }, [])

  const isBackgroundVisible =
    showBackground ||
    staticHeader ||
    (showSidebarNav &&
      scrollPos > OPAQUE_BACKGROUND_THRESHOLD &&
      scrollAnchor > OPAQUE_BACKGROUND_THRESHOLD)

  const onScroll = useCallback(() => {
    //We will update the scroll position state here
    setScrollPos((prevScrollPos) => {
      if (window.scrollY < prevScrollPos) {
        //we're scrolling up, reset anchor position if scroll delta (anchor - current position) is negative
        setScrollAnchor((prevScrollAnchor) =>
          prevScrollAnchor - window.scrollY < 0
            ? window.scrollY
            : prevScrollAnchor
        )
      } else if (window.scrollY > prevScrollPos) {
        //we're scrolling down, reset anchor position if scroll delta is positive
        setScrollAnchor((prevScrollAnchor) =>
          prevScrollAnchor - window.scrollY >= 0
            ? window.scrollY
            : prevScrollAnchor
        )
      }

      return window.scrollY > 0 ? window.scrollY : 0
    })
  }, [setScrollAnchor, setScrollPos])

  useLayoutEffect(() => {
    //Show or hide the header based on distance from the scroll anchor position
    if (showSidebarNav) {
      if (!isHidden && scrollPos - scrollAnchor > SCROLL_THRESHOLD) {
        //hide after scrolling 30px down
        setIsHidden(true)
      }

      if (isHidden && scrollPos - scrollAnchor < -SCROLL_THRESHOLD) {
        //show after scrolling 30px up
        setIsHidden(false)
      }
    } else if (isHidden) {
      //always visible on desktop
      setIsHidden(false)
    }
  }, [scrollPos, scrollAnchor, showSidebarNav, isHidden])

  useEffect(() => {
    window.addEventListener("scroll", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
    }
  }, [])

  const [isLightboxVisible, toggleLightbox] = useLightboxManager()

  return (
    <>
      <HeaderWrapper
        id="nav-header"
        //@ts-ignore
        showBackground={isBackgroundVisible}
        scrollY={scrollPos}
        isHidden={isHidden && !staticHeader}
        headerPosition={staticHeader ? "relative" : null}
        isBannerVisible={bannerText !== ""}
      >
        <Banner visible={bannerText !== ""}>{bannerText}</Banner>
        <HeaderInner>
          <Navigation>
            <ImageContainer to="/">
              <img src={mappedinLogoSrc} alt="Mappedin Logo" />
            </ImageContainer>
            <RightButtons>
              {/* Render the dropdown menus */}
              {NavigationConfig.map((category, idx) => {
                return <DropdownMenu key={idx} category={category} />
              })}
              <a href={externalLinks.cms} alt="Sign In" rel="nofollow">
                Sign In
              </a>
              {contactLightbox ? (
                <button onClick={toggleLightbox}>Contact Us</button>
              ) : (
                <Link to="contact-form" smooth={true} duration={1000}>
                  Contact Us
                </Link>
              )}
            </RightButtons>
            <HamburgerIcon
              onClick={() =>
                dispatch((prevState) => ({
                  ...prevState,
                  showSidebarNavigation: !prevState.showSidebarNavigation,
                }))
              }
            />
          </Navigation>
        </HeaderInner>
      </HeaderWrapper>
      {isLightboxVisible && (
        <ContactLightbox toggle={toggleLightbox}>
          <ContactSection />
        </ContactLightbox>
      )}
      {bannerText !== "" && (
        <Spacer
          height={{ sm: "48px", md: "24px", lg: "32px" }}
          style={{ backgroundColor: "#282828" }}
        />
      )}
    </>
  )
}

export default React.memo(Header)
